import React from 'react';
import { DataContext } from '../DataProvider/DataProvider';
import styled from 'styled-components';

function NotesInput() {
  const { currentNode, updateNodeComment, currentMode } =
    React.useContext(DataContext);

  const [isEditing, setIsEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState('');

  React.useEffect(() => {
    if (currentNode) {
      setEditValue(currentNode.comment || '');
    }
  }, [currentNode]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = () => {
    if (updateNodeComment && currentNode) {
      updateNodeComment(currentNode, editValue);
      setIsEditing(false);
    }
  };

  const handleCancel = () => {
    setEditValue(currentNode?.comment || '');
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && e.ctrlKey) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  if (currentMode === 'trainer') {
    return null;
  }

  if (!currentNode) {
    return (
      <NotesContainer>
        <NotesHeader>Notes</NotesHeader>
        <NotesPlaceholder>Select a position to add notes</NotesPlaceholder>
      </NotesContainer>
    );
  }

  const isRootNode = !currentNode.move;
  const positionDescription = isRootNode
    ? 'Initial Position'
    : `After ${currentNode.move.san}`;

  return (
    <NotesContainer>
      <NotesHeader>
        Notes for {positionDescription}
        {currentNode.comment && <NoteIndicator>*</NoteIndicator>}
      </NotesHeader>

      {isEditing ? (
        <NotesEditArea>
          <NotesTextarea
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder='Add your note. Ctrl+Enter saves. Esc cancels.'
            autoFocus
          />
          <NotesEditButtons>
            <SaveButton onClick={handleSave}>Save</SaveButton>
            <CancelButton onClick={handleCancel}>Cancel</CancelButton>
          </NotesEditButtons>
        </NotesEditArea>
      ) : (
        <NotesDisplayArea>
          {currentNode.comment ? (
            <NotesText>{currentNode.comment}</NotesText>
          ) : (
            <NotesPlaceholder>No note yet</NotesPlaceholder>
          )}
          <EditButton onClick={handleEdit}>Edit note</EditButton>
        </NotesDisplayArea>
      )}
    </NotesContainer>
  );
}

export default NotesInput;

const NotesContainer = styled.div`
  padding: 22px;
  border: 1px solid var(--border);
  border-radius: 26px;
  background: var(--surface);
  box-shadow: var(--shadow-md);
`;

const NotesHeader = styled.div`
  font-weight: 600;
  margin-bottom: 14px;
  color: var(--text);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const NoteIndicator = styled.span`
  color: var(--accent);
  font-weight: bold;
  font-size: 1.2em;
`;

const NotesEditArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotesTextarea = styled.textarea`
  width: 100%;
  min-height: 120px;
  padding: 14px 16px;
  border: 1px solid var(--border);
  border-radius: 16px;
  font-family: inherit;
  font-size: 0.9rem;
  resize: vertical;
  color: var(--text);
  background: rgba(255, 249, 241, 0.96);

  &:focus {
    outline: none;
    border-color: rgba(159, 109, 76, 0.36);
    box-shadow: 0 0 0 4px rgba(159, 109, 76, 0.14);
  }
`;

const NotesEditButtons = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const SaveButton = styled.button`
  padding: 10px 16px;
  background: linear-gradient(180deg, #b17a58 0%, #8f5c3e 100%);
  color: #fffaf5;
  border: 1px solid rgba(143, 92, 62, 0.4);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: linear-gradient(180deg, #ba8461 0%, #986145 100%);
  }
`;

const CancelButton = styled.button`
  padding: 10px 16px;
  background: rgba(159, 109, 76, 0.12);
  color: var(--accent-strong);
  border: 1px solid rgba(159, 109, 76, 0.18);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(159, 109, 76, 0.18);
  }
`;

const NotesDisplayArea = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const NotesText = styled.div`
  background: rgba(255, 249, 241, 0.94);
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid var(--border);
  border-left: 3px solid rgba(159, 109, 76, 0.36);
  white-space: pre-wrap;
  line-height: 1.5;
  color: var(--text);
`;

const NotesPlaceholder = styled.div`
  color: var(--text-faint);
  font-style: italic;
  padding: 12px;
  text-align: center;
`;

const EditButton = styled.button`
  padding: 10px 16px;
  background: rgba(159, 109, 76, 0.12);
  color: var(--accent-strong);
  border: 1px solid rgba(159, 109, 76, 0.18);
  border-radius: 999px;
  cursor: pointer;
  font-size: 0.9rem;
  align-self: flex-end;

  &:hover {
    background: rgba(159, 109, 76, 0.18);
  }
`;
